import type { ContextVariable } from '@/components/atoms/ContextVariables/ContextVariables';

const indentJson = (json: string): string => {
  const pad = (depth: number) => '\n' + '  '.repeat(depth);
  let out = '';
  let depth = 0;
  let inString = false;
  let escaped = false;

  for (const char of json) {
    if (inString) {
      out += char;
      if (escaped) {
        escaped = false;
      } else if (char === '\\') {
        escaped = true;
      } else if (char === '"') {
        inString = false;
      }
      continue;
    }
    if (char === '"') {
      inString = true;
      out += char;
    } else if (char === '{' || char === '[') {
      depth += 1;
      out += char + pad(depth);
    } else if (char === '}' || char === ']') {
      depth -= 1;
      const body = out.replace(/\s+$/, '');
      out = /[{[]$/.test(body) ? body + char : body + pad(depth) + char;
    } else if (char === ',') {
      out += char + pad(depth);
    } else if (char === ':') {
      out += ': ';
    } else if (!/\s/.test(char)) {
      out += char;
    }
  }

  return out;
};

/** A string holding JSON is indented too, so a serialized value reads like the real thing. */
export const formatContextValue = (value: unknown): string => {
  if (typeof value !== 'string') {
    return JSON.stringify(value, null, 2);
  }
  const trimmed = value.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) {
    return value;
  }
  try {
    JSON.parse(trimmed);
  } catch {
    return value;
  }
  return indentJson(trimmed);
};

const variableType = (value: unknown, formatted: string): ContextVariable['type'] => {
  if (formatted.includes('\n')) {
    return 'code';
  }
  return typeof value === 'number' ? 'number' : 'text';
};

export const toContextVariables = (context: Record<string, unknown>): ContextVariable[] =>
  Object.entries(context).map(([name, value]) => {
    const formatted = formatContextValue(value);
    return { name, value: formatted, type: variableType(value, formatted) };
  });
